import React, { useState } from 'react';
import { Box, styled } from '@mui/material';
import { authColors, authFontFamily, authType } from 'app/pages/auth/shared/authTheme';
import { replayChrome, sentryMask } from 'sentryReplayPrivacy';

/** Material 3 outlined text field used across the redesigned auth screens. */

const Label = styled('label')({
  ...authType.label,
  fontFamily: authFontFamily,
  display: 'block',
});

const InputShell = styled('div')<{ 'data-state': 'default' | 'focus' | 'error' }>(
  ({ 'data-state': state }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    height: 40,
    paddingLeft: 16,
    paddingRight: 8,
    borderRadius: 4,
    border: `1px solid ${
      state === 'error' ? authColors.error : state === 'focus' ? authColors.primary : authColors.inputBorder
    }`,
    transition: 'border-color 120ms ease',
    backgroundColor: authColors.white,
  })
);

const Input = styled('input')({
  flex: '1 0 0',
  minWidth: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  padding: '4px 0',
  fontFamily: authFontFamily,
  ...authType.input,
  color: authColors.textStrong,
  '&::placeholder': { color: authColors.textMuted, opacity: 1 },
  // keep password-manager autofill from painting a blue background
  '&:-webkit-autofill': { WebkitBoxShadow: `0 0 0 1000px ${authColors.white} inset` },
});

const HelperRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  minHeight: 20,
  width: '100%',
});

const ErrorText = styled('span')({
  ...authType.helper,
  fontFamily: authFontFamily,
  color: authColors.error,
});

const LinkButton = styled('button')({
  marginLeft: 'auto',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  ...authType.helper,
  fontFamily: authFontFamily,
  color: authColors.link,
});

export interface AuthTextFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Error message shown in red below the field (also reddens the border). */
  error?: string | null;
  /** Trailing content inside the field (e.g. a password visibility toggle). */
  endAdornment?: React.ReactNode;
  /** Right-aligned link shown in the helper row (e.g. "Forgot password?"). */
  trailingLink?: { text: string; onClick: () => void };
  inputRef?: React.Ref<HTMLInputElement>;
}

export default function AuthTextField({
  label,
  value,
  onChange,
  error,
  endAdornment,
  trailingLink,
  inputRef,
  onFocus,
  onBlur,
  ...inputProps
}: AuthTextFieldProps) {
  const [focused, setFocused] = useState(false);
  const state = error ? 'error' : focused ? 'focus' : 'default';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
      <Label htmlFor={inputProps.id ?? inputProps.name}>{label}</Label>
      <InputShell data-state={state}>
        <Input
          {...inputProps}
          id={inputProps.id ?? inputProps.name}
          {...sentryMask}
          ref={inputRef}
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            setFocused(false);
            onBlur?.(e);
          }}
        />
        {endAdornment}
      </InputShell>
      {(error || trailingLink) && (
        <HelperRow>
          {error && <ErrorText {...replayChrome}>{error}</ErrorText>}
          {trailingLink && (
            <LinkButton type="button" onClick={trailingLink.onClick} {...replayChrome}>
              {trailingLink.text}
            </LinkButton>
          )}
        </HelperRow>
      )}
    </Box>
  );
}
